import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useApi from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useCartDrawer } from "../context/CartDrawerContext";
import { resolveAssetUrl } from "../utils/assetUrl";

const Product = () => {
  const [mockTests, setMockTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { user } = useAuth();
  const { bearerAuth } = useApi();
  const bearerAuthRef = useRef(bearerAuth);
  useEffect(() => {
    // keep latest function without retriggering effect
    bearerAuthRef.current = bearerAuth;
  }, [bearerAuth]);

  const { openCart } = useCartDrawer();

  const { addToCart } = useCart();

  // Fetch products
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await bearerAuthRef.current("product");
        const data = res?.data?.data || res?.data?.results || res?.data || [];
        if (mounted) setMockTests(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!mounted) return;
        setError(
          err?.response?.data?.message || err.message || "Error fetching products"
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
    // Intentionally only depends on user id to avoid loops caused by unstable functions
  }, [user?.id]);

  return (
    <>
      {/* Loading & Error */}
      {loading && <div className="loading">Loading…</div>}
      {error && <div className="error">{error}</div>}

      {/* Product Section */}
      <section className="products">
        <div className="container">
          <h2>Our Products</h2>
          <div className="grid">
            {mockTests.map((p) => {
              const image = resolveAssetUrl(
                p.image_url || p.image,
                `${process.env.PUBLIC_URL}/assets/images/service/service-1.jpg`
              );
              const price = parseFloat(p.price) || 0;
              const outOfStock = (p.stock ?? 0) <= 0;

              return (
                <div key={p.id} className="product-card">
                  <img
                    src={image}
                    alt={p.title}
                    onError={(e) => {
                      e.currentTarget.src =
                        process.env.PUBLIC_URL +
                        "/assets/images/service/placeholder.png";
                    }}
                  />
                  <h4>{p.title}</h4>
                  <p className="desc">{p.description}</p>
                  <p className="price">
                    {p.currency || "INR"} {price.toFixed(2)}
                  </p>
                  <p className={`stock ${outOfStock ? "out" : "in"}`}>
                    {outOfStock ? "Out of Stock" : `In Stock: ${p.stock}`}
                  </p>

                  <button
                    className="add-btn"
                    disabled={outOfStock}
                    onClick={() => {
                      addToCart(p);
                      openCart(); // open global drawer on add
                    }}
                  >
                    {outOfStock ? "Out of Stock" : "Add to Cart"}
                  </button>

                  <Link to={`/product-details/${p.slug}`} className="details">
                    View Details →
                  </Link>
                </div>
              );
            })}

            {!loading && mockTests.length === 0 && (
              <p className="no-products">No products found.</p>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .loading, .error { text-align:center; padding:10px; }
        .products { padding: 50px 0; }
        .container { width:90%; margin:auto; }
        h2 { text-align:center; margin-bottom:40px; }

        .grid {
          display:grid;
          grid-template-columns:repeat(auto-fill, minmax(280px,1fr));
          gap:25px;
        }

        .product-card {
          border:1px solid #eee;
          border-radius:12px;
          padding:15px;
          text-align:center;
          background:#fff;
          box-shadow:0 2px 10px rgba(0,0,0,0.05);
          transition:transform 0.3s ease;
        }
        .product-card:hover { transform:translateY(-5px); }

        .product-card img {
          width:100%;
          height:200px;
          object-fit:cover;
          border-radius:10px;
        }
        .price { color:#28a745; font-weight:bold; }
        .desc { font-size:0.9rem; min-height:40px; }
        .stock.in { color:#28a745; font-weight:bold; }
        .stock.out { color:#dc3545; font-weight:bold; }
        .add-btn {
          width:100%;
          background:#28a745;
          color:#fff;
          border:none;
          padding:10px;
          border-radius:8px;
          margin-top:10px;
          cursor:pointer;
          font-weight:bold;
        }
        .add-btn:disabled { background:#aaa; cursor:not-allowed; }
        .details {
          display:block;
          margin-top:10px;
          text-decoration:none;
          color:#007bff;
        }
      `}</style>
    </>
  );
};

export default Product;
